import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { FormError } from "@/components/form-error";
import { SkipButton } from "@/components/onboarding/skip-button";
import { StepSuccess } from "@/components/onboarding/step-success";
import { IPhoneFrame } from "@/components/onboarding/iphone-frame";
import { useSendWelcomeNotificationMutation } from "@/hooks/use-notifications";
import { useServiceQuery } from "@/hooks/use-services";
import { useServiceStore } from "@/stores/service-store";

interface OnboardingSendNotificationProps {
  onSkip: () => void;
  onSuccess: () => void;
  initialSent?: boolean;
}

function NotificationPreview({ logoUrl, serviceName }: { logoUrl?: string; serviceName?: string }) {
  return (
    <div className="flex justify-center">
      <div className="relative h-[180px] overflow-hidden [mask-image:linear-gradient(to_bottom,black_60%,transparent)]">
        <IPhoneFrame>
          <div className="px-3 pt-2">
            <div className="rounded-2xl bg-white/90 backdrop-blur-xl p-2.5 shadow-lg">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 shrink-0 rounded-[8px] flex items-center justify-center overflow-hidden">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt="Service logo"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-white font-bold text-xl">A</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-[14px] font-semibold text-gray-900">
                      {serviceName || "Service Title"}
                    </p>
                    <span className="text-[12px] text-gray-400">now</span>
                  </div>
                  <p className="text-[14px] font-semibold text-gray-900">
                    Welcome to Appio
                  </p>
                  <p className="text-[14px] text-gray-600">
                    Great to have you
                  </p>
                </div>
              </div>
            </div>
          </div>
        </IPhoneFrame>
      </div>
    </div>
  );
}

export function OnboardingSendNotification({
  onSkip,
  onSuccess,
  initialSent = false,
}: OnboardingSendNotificationProps) {
  const navigate = useNavigate();
  const { selectedService, selectedServiceId, setSelectedService } = useServiceStore();
  const sendMutation = useSendWelcomeNotificationMutation();
  const [isSent, setIsSent] = useState(initialSent);

  // After a page refresh the store has only the ID, not the service object.
  const { data: fetchedService } = useServiceQuery(selectedServiceId ?? "");

  useEffect(() => {
    if (fetchedService && !selectedService) {
      setSelectedService(fetchedService);
    }
  }, [fetchedService, selectedService, setSelectedService]);

  const service = selectedService ?? fetchedService;

  const handleSendNotification = async () => {
    try {
      await sendMutation.mutateAsync();
      setIsSent(true);
      onSuccess();
    } catch {
      // Error is handled by mutation state
    }
  };

  const handleContinue = () => {
    navigate("/onboarding/widget");
  };

  if (isSent) {
    return (
      <div className="max-w-md mx-auto space-y-6">
        <StepSuccess
          title="Notification sent!"
          description="Check the notification on your device."
        />

        <NotificationPreview logoUrl={service?.logo_url} serviceName={service?.title} />

        <div className="flex justify-center">
          <Button onClick={handleContinue}>Next</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto space-y-6">
      {sendMutation.isError && (
        <FormError>Failed to send notification. Please try again.</FormError>
      )}

      <div className="flex justify-center py-8">
        <Button
          size="lg"
          onClick={handleSendNotification}
          disabled={sendMutation.isPending}
        >
          {sendMutation.isPending ? (
            <>
              <Loader2 className="animate-spin" />
              Sending...
            </>
          ) : (
            "Send Notification"
          )}
        </Button>
      </div>

      <NotificationPreview logoUrl={service?.logo_url} serviceName={service?.title} />

      <div className="flex justify-center">
        <SkipButton onClick={onSkip} />
      </div>
    </div>
  );
}
